/**
 * PercentageInput - Form-friendly percentage input with formatting
 *
 * Features:
 * - Formats with % suffix on blur
 * - Allows raw number entry during focus
 * - Validation integration with error display
 * - Configurable decimal precision
 * - Handles null/undefined values
 */

import * as React from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

export interface PercentageInputProps {
  /** Current value (as percentage, e.g., 5.5 for 5.5%) */
  value: number | null | undefined;
  /** Change handler */
  onChange: (value: number | null) => void;
  /** Error message */
  error?: string;
  /** Label text */
  label?: string;
  /** Whether field is required */
  required?: boolean;
  /** Placeholder text */
  placeholder?: string;
  /** Input ID */
  id?: string;
  /** Whether the input is disabled */
  disabled?: boolean;
  /** Minimum value (default 0) */
  min?: number;
  /** Maximum value (default 100) */
  max?: number;
  /** Number of decimal places (default 3) */
  decimals?: number;
  /** Allow negative values */
  allowNegative?: boolean;
  /** Additional className for the container */
  className?: string;
  /** Additional className for the input */
  inputClassName?: string;
  /** Show inline (without label wrapper) */
  inline?: boolean;
  /** Size variant */
  size?: 'sm' | 'default' | 'lg';
}

/**
 * Format a number as percentage string
 */
function formatPercentage(value: number | string | null | undefined, decimals = 3): string {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  // Convert to number if it's a string
  const numValue = typeof value === 'string' ? Number.parseFloat(value) : value;
  if (isNaN(numValue)) {
    return '';
  }
  return numValue.toFixed(decimals);
}

/**
 * Parse percentage string to number
 */
function parsePercentage(input: string): number | null {
  if (!input || input.trim() === '') {
    return null;
  }
  // Remove % sign if present
  const cleaned = input.replace(/%/g, '').trim();
  if (cleaned === '' || cleaned === '-') {
    return null;
  }
  const num = Number.parseFloat(cleaned);
  return isNaN(num) ? null : num;
}

/**
 * PercentageInput - Form-friendly percentage input
 */
export const PercentageInput: React.FC<PercentageInputProps> = ({
  value,
  onChange,
  error,
  label,
  required = false,
  placeholder = '0.000',
  id,
  disabled = false,
  min = 0,
  max = 100,
  decimals = 3,
  allowNegative = false,
  className,
  inputClassName,
  inline = false,
  size = 'default',
}) => {
  const inputId = id || React.useId();
  const [isFocused, setIsFocused] = React.useState(false);
  const [displayValue, setDisplayValue] = React.useState(() => formatPercentage(value, decimals));

  // Update display value when external value changes (and not focused)
  React.useEffect(() => {
    if (!isFocused) {
      setDisplayValue(formatPercentage(value, decimals));
    }
  }, [value, isFocused, decimals]);

  const handleFocus = () => {
    setIsFocused(true);
    // Show raw number on focus for easier editing
    if (value !== null && value !== undefined) {
      setDisplayValue(value.toString());
    }
  };

  const handleBlur = () => {
    setIsFocused(false);
    const parsed = parsePercentage(displayValue);

    // Validate and clamp
    let finalValue = parsed;
    if (finalValue !== null) {
      if (!allowNegative && finalValue < 0) {
        finalValue = 0;
      }
      if (min !== undefined && finalValue < min) {
        finalValue = min;
      }
      if (max !== undefined && finalValue > max) {
        finalValue = max;
      }
      // Round to specified decimals
      finalValue = Math.round(finalValue * Math.pow(10, decimals)) / Math.pow(10, decimals);
    }

    onChange(finalValue);
    setDisplayValue(formatPercentage(finalValue, decimals));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;

    // Allow only numbers, decimals, minus, and empty
    if (inputValue === '' || /^-?\d*\.?\d*$/.test(inputValue)) {
      setDisplayValue(inputValue);
    }
  };

  const sizeClasses = {
    sm: 'h-8 text-sm',
    default: 'h-10',
    lg: 'h-12 text-lg',
  };

  const inputElement = (
    <div className="relative">
      <Input
        id={inputId}
        type="text"
        inputMode="decimal"
        value={displayValue}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={placeholder}
        disabled={disabled}
        aria-invalid={!!error}
        aria-describedby={error ? `${inputId}-error` : undefined}
        className={cn(
          'pr-7 text-right',
          sizeClasses[size],
          error && 'border-red-500 focus:border-red-500 focus:ring-red-500',
          inputClassName,
        )}
      />
      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none">
        %
      </span>
    </div>
  );

  if (inline) {
    return (
      <div className={className}>
        {inputElement}
        {error && (
          <p id={`${inputId}-error`} className="text-xs text-red-600 mt-1">
            {error}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className={cn('space-y-1.5', className)}>
      {label && (
        <Label htmlFor={inputId}>
          {label} {required && <span className="text-red-500">*</span>}
        </Label>
      )}
      {inputElement}
      {error && (
        <p id={`${inputId}-error`} className="text-sm text-red-600">
          {error}
        </p>
      )}
    </div>
  );
};

PercentageInput.displayName = 'PercentageInput';

/**
 * Validate a percentage value
 */
export function validatePercentageValue(
  value: number | null | undefined,
  options: {
    required?: boolean;
    min?: number;
    max?: number;
    fieldName?: string;
  } = {},
): string | undefined {
  const { required = false, min = 0, max = 100, fieldName = 'Value' } = options;

  if (value === null || value === undefined) {
    if (required) {
      return `${fieldName} is required`;
    }
    return undefined;
  }

  if (isNaN(value)) {
    return `${fieldName} must be a valid number`;
  }

  if (value < min) {
    return `${fieldName} must be at least ${min}%`;
  }

  if (value > max) {
    return `${fieldName} cannot exceed ${max}%`;
  }

  return undefined;
}

export { formatPercentage, parsePercentage };
